import { Alert, Box, Button, Chip, Divider, Grid, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getAllInvoiceGroups } from 'src/shared/services/invoiceGroupService';
import { getAllInvoiceTemplate, getInvoiceTemplateById } from 'src/shared/services/invoiceService';
import { InvoiceTemplate, Sending } from 'src/modules/invoice/models/invoiceTemplate';
import EmailIcon from '@mui/icons-material/Email';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface InvoiceGroupSummary {
  ownerId: number;
  name: string;
  templates: InvoiceTemplate[];
}

type OnboardingStepInvoiceTemplatesProps = {
  onNavigateAway?: () => void;
};

export const OnboardingStepInvoiceTemplates = ({ onNavigateAway }: OnboardingStepInvoiceTemplatesProps) => {
  const { t } = useTranslation(['onboarding']);
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();
  const [groups, setGroups] = useState<InvoiceGroupSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsRes, templatesRes] = await Promise.all([getAllInvoiceGroups(0, 100, 'name', 'asc', ''), getAllInvoiceTemplate()]);
      if (isMounted()) {
        const allGroups = groupsRes.data.results || [];
        const memberTemplates = (templatesRes.data.results || []).filter((tmpl: InvoiceTemplate) => tmpl.memberType === 1);
        // Hämta varje mall individuellt för att få sendings
        const detailedTemplates = await Promise.all(
          memberTemplates.map(async (tmpl: InvoiceTemplate) => {
            try {
              const detail = await getInvoiceTemplateById(tmpl.template_id);
              return detail.data;
            } catch {
              return tmpl;
            }
          }),
        );
        const summary: InvoiceGroupSummary[] = allGroups.map((g: { owner_id: number; name: string }) => ({
          ownerId: g.owner_id,
          name: g.name,
          templates: detailedTemplates.filter((tmpl) => tmpl.owner_id === g.owner_id),
        }));
        setGroups(summary);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const renderPdfPreview = (sending: Sending) => {
    const heading = sending.defaultHeading || '';
    const text = sending.defaultText || '';
    return (
      <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
        {/* Faktura-"papper" */}
        <Box sx={{ p: 2.5 }}>
          {/* Topp: logotyp-skeleton + referens/datum */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Skeleton variant='rounded' width={80} height={28} />
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.65rem' }}>
                Betalningsreferens/OCR
              </Typography>
              <Typography variant='caption' display='block' fontWeight={600} sx={{ fontSize: '0.7rem' }}>
                645799297
              </Typography>
            </Box>
          </Box>
          {/* Datum-rad */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 1.5 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.6rem' }}>
                Utskriftsdatum
              </Typography>
              <Typography variant='caption' display='block' sx={{ fontSize: '0.65rem' }}>
                2026-04-07
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.6rem' }}>
                Betalas senast
              </Typography>
              <Typography variant='caption' display='block' fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                2026-05-12
              </Typography>
            </Box>
          </Box>
          {/* Adress-skeleton */}
          <Box sx={{ ml: 'auto', width: '45%', mb: 2 }}>
            <Skeleton variant='text' width='70%' height={12} />
            <Skeleton variant='text' width='50%' height={12} />
            <Skeleton variant='text' width='60%' height={12} />
          </Box>
          {/* Faktura innehåll - rubrik */}
          <Typography
            variant='caption'
            fontWeight={700}
            sx={{ fontSize: '0.7rem', borderBottom: 1, borderColor: 'grey.300', pb: 0.5, mb: 1, display: 'block' }}
          >
            Faktura innehåll
          </Typography>
          {/* Faktura-rad */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: 1, borderColor: 'grey.200' }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.6rem' }}>
                15437
              </Typography>
              <Skeleton variant='text' width={60} height={12} />
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.6rem' }}>
                Avgift huvudmedlem
              </Typography>
            </Box>
            <Typography variant='caption' fontWeight={600} sx={{ fontSize: '0.65rem' }}>
              250 SEK
            </Typography>
          </Box>
          {/* Text på faktura (defaultHeading + defaultText) */}
          <Box sx={{ mt: 2, mb: 2, minHeight: 40 }}>
            {heading ? (
              <Typography variant='caption' fontWeight={600} sx={{ fontSize: '0.7rem', mb: 0.5, display: 'block' }}>
                {heading}
              </Typography>
            ) : (
              <Typography variant='caption' color='text.disabled' sx={{ fontSize: '0.7rem', mb: 0.5, display: 'block' }}>
                {t('onboarding:stepContent.invoiceTemplates.noHeading')}
              </Typography>
            )}
            {text ? (
              <Typography
                variant='caption'
                color='text.secondary'
                component='div'
                sx={{ fontSize: '0.65rem' }}
                dangerouslySetInnerHTML={{ __html: text }}
              />
            ) : (
              <Typography variant='caption' color='text.disabled' sx={{ fontSize: '0.65rem' }}>
                {t('onboarding:stepContent.invoiceTemplates.noText')}
              </Typography>
            )}
          </Box>
          {/* Sidfot-skeleton (organisationsinfo) */}
          <Box sx={{ borderTop: 1, borderColor: 'grey.200', pt: 1.5, mt: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Skeleton variant='text' width='80%' height={10} />
                <Skeleton variant='text' width='60%' height={10} />
                <Skeleton variant='text' width='70%' height={10} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Skeleton variant='text' width='70%' height={10} />
                <Skeleton variant='text' width='90%' height={10} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Skeleton variant='text' width='60%' height={10} />
                <Skeleton variant='text' width='80%' height={10} />
              </Box>
            </Box>
          </Box>
          {/* Inbetalnings-avi skeleton */}
          <Box sx={{ mt: 1.5, border: 1, borderColor: 'grey.300', borderRadius: 0.5, p: 1.5, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.55rem', textTransform: 'uppercase' }}>
                  Inbetalning/Girering avi
                </Typography>
                <Skeleton variant='text' width={100} height={10} sx={{ mt: 0.5 }} />
              </Box>
              <Box sx={{ border: 1, borderColor: 'grey.400', px: 1.5, py: 0.5 }}>
                <Typography variant='caption' fontWeight={700} sx={{ fontSize: '0.75rem' }}>
                  OCR
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSending = (sending: Sending) => {
    const heading = sending.emailHeading || '';
    const text = sending.emailText || '';
    return (
      <Grid container spacing={2} key={sending.templateSendings_id} sx={{ mt: 0.5 }}>
        {/* Vänster: e-postförhandsvisning */}
        <Grid item xs={12} md={6}>
          <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
            {t('onboarding:stepContent.invoiceTemplates.emailPreview')}
          </Typography>
          <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, overflow: 'hidden' }}>
            {/* Mail header */}
            <Box sx={{ bgcolor: 'grey.100', px: 2, py: 1.5, borderBottom: 1, borderColor: 'grey.300' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <EmailIcon fontSize='small' color='action' />
                <Typography variant='caption' fontWeight={600} color='text.secondary'>
                  {t('onboarding:stepContent.invoiceTemplates.from')}: {currentUser?.customerName || ''}
                </Typography>
              </Box>
              <Typography variant='body2' fontWeight={600}>
                {heading || t('onboarding:stepContent.invoiceTemplates.noHeading')}
              </Typography>
            </Box>
            {/* Mail body */}
            <Box sx={{ px: 2, py: 2, bgcolor: '#fff' }}>
              {text ? (
                <Typography variant='body2' color='text.secondary' component='div' dangerouslySetInnerHTML={{ __html: text }} />
              ) : (
                <Typography variant='body2' color='text.disabled'>
                  {t('onboarding:stepContent.invoiceTemplates.noText')}
                </Typography>
              )}
              {/* Gå till betalning-knapp */}
              <Box sx={{ mt: 2, mb: 1 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: 'grey.700',
                    color: '#fff',
                    px: 2,
                    py: 0.75,
                    borderRadius: 1,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                  }}
                >
                  {t('onboarding:stepContent.invoiceTemplates.goToPayment')}
                </Box>
              </Box>
            </Box>
            {/* Bifogad fil */}
            <Box sx={{ bgcolor: 'grey.50', px: 2, py: 1, borderTop: 1, borderColor: 'grey.300', display: 'flex', alignItems: 'center', gap: 1 }}>
              <PictureAsPdfIcon fontSize='small' color='error' />
              <Typography variant='caption' color='text.secondary'>
                Faktura.pdf
              </Typography>
            </Box>
          </Box>
        </Grid>
        {/* Höger: PDF-faktura förhandsvisning */}
        <Grid item xs={12} md={6}>
          <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
            {t('onboarding:stepContent.invoiceTemplates.pdfPreview')}
          </Typography>
          {renderPdfPreview(sending)}
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
            <Typography variant='body1' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.invoiceTemplates.description')}
            </Typography>
            <Button
              variant='outlined'
              size='small'
              startIcon={<OpenInNewIcon />}
              href='https://hjalp.foreningssupport.se/article/357-installningar-fakturering-fakturamallar'
              target='_blank'
              rel='noopener noreferrer'
            >
              {t('onboarding:readMoreHelp')}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography variant='h6' fontWeight={600} sx={{ mb: 1 }}>
            {t('onboarding:stepContent.invoiceTemplates.groupsTitle')}
          </Typography>
          <Box sx={{ bgcolor: '#fff', borderRadius: 1, p: 2, border: 1, borderColor: 'grey.300', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}>
            <Alert severity='warning' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.invoiceTemplates.editHintPrefix')}{' '}
              <Button
                variant='text'
                size='small'
                sx={{
                  p: 0,
                  minWidth: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  verticalAlign: 'baseline',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                }}
                onClick={() => {
                  if (onNavigateAway) onNavigateAway();
                }}
              >
                {t('onboarding:stepContent.invoiceTemplates.editHintLink')}
              </Button>
              .
            </Alert>
            {loading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Skeleton variant='rounded' height={60} />
                <Skeleton variant='rounded' height={60} />
              </Box>
            )}
            {!loading && groups.length === 0 && <Alert severity='warning'>{t('onboarding:stepContent.invoiceTemplates.noGroups')}</Alert>}
            {!loading && groups.length > 0 && (
              <Box>
                {groups.map((group) => (
                  <Box key={group.ownerId} sx={{ mb: 2 }}>
                    <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 0.5 }}>
                      {t('onboarding:stepContent.invoiceTemplates.groupPrefix')}: {group.name}
                    </Typography>
                    {group.templates.length === 0 && (
                      <Typography variant='body2' color='text.secondary'>
                        {t('onboarding:stepContent.invoiceTemplates.noTemplates')}
                      </Typography>
                    )}
                    {group.templates.map((tmpl) => {
                      const emailSending = tmpl.sendings?.find((s) => s.sendingType === 2);
                      const sending = emailSending || (tmpl.sendings && tmpl.sendings.length > 0 ? tmpl.sendings[0] : null);
                      const hasUnapproved = tmpl.sendings?.some((s) => s.notReady || s.textApproved === false);
                      return (
                        <Box key={tmpl.template_id} sx={{ pb: 2 }}>
                          <Chip
                            label={`${t('onboarding:stepContent.invoiceTemplates.templatePrefix')}: ${tmpl.name}`}
                            size='small'
                            variant='outlined'
                            sx={{ mb: 1 }}
                          />
                          {hasUnapproved && (
                            <Alert severity='error' sx={{ mb: 1 }}>
                              {t('onboarding:stepContent.invoiceTemplates.notApproved')}
                            </Alert>
                          )}
                          {sending && renderSending(sending)}
                        </Box>
                      );
                    })}
                    <Divider />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
